// COMPONENTS & FUNCTIONS
import { convertHandler } from "../../functions/mathHandler";

// INTERFACES
import {
  GraphControls,
  GraphLegend,
  GraphVariable,
} from "../../interface/functionalityInterface";

export interface GraphPoint {
  x: number;
  y: number | null;
}

export interface GraphLegendResult {
  legendId: string;
  points: GraphPoint[];
  error?: string;
}

export const validateGraphControls = (
  controls: GraphControls
): string | null => {
  if (
    !Number.isFinite(controls.xMin) ||
    !Number.isFinite(controls.xMax) ||
    controls.xMax <= controls.xMin
  ) {
    return "X max must be greater than X min.";
  }
  if (!Number.isFinite(controls.xDelta) || controls.xDelta <= 0) {
    return "X delta must be positive.";
  }
  if (
    !Number.isFinite(controls.trackingIncrement) ||
    controls.trackingIncrement <= 0
  ) {
    return "Cursor tracking increment must be positive.";
  }
  if (
    controls.yMin !== null &&
    !Number.isFinite(controls.yMin)
  ) {
    return "Y min must be a valid number.";
  }
  if (
    controls.yMax !== null &&
    !Number.isFinite(controls.yMax)
  ) {
    return "Y max must be a valid number.";
  }
  if (
    controls.yMin !== null &&
    controls.yMax !== null &&
    controls.yMax <= controls.yMin
  ) {
    return "Y max must be greater than Y min.";
  }
  if (
    !Number.isInteger(controls.xDecimals) ||
    controls.xDecimals < 0 ||
    controls.xDecimals > 9
  ) {
    return "X decimals must be between 0 and 9.";
  }
  return null;
};

export const createTrackingValues = (
  controls: GraphControls
): number[] | null => {
  const pointCount =
    Math.floor(
      (controls.xMax - controls.xMin) /
        controls.trackingIncrement
    ) + 1;
  if (pointCount < 2 || pointCount > 1000) return null;

  const values = Array.from({ length: pointCount }, (_, index) =>
    Number(
      (
        controls.xMin +
        index * controls.trackingIncrement
      ).toPrecision(12)
    )
  );
  const lastValue = values[values.length - 1];
  if (lastValue < controls.xMax && values.length < 1000) {
    values.push(controls.xMax);
  }
  return values;
};

const valueFromBaseUnit = (
  variable: GraphVariable,
  value: number
) =>
  convertHandler(
    value.toString(),
    variable.selectedUnit,
    variable.baseUnit,
    variable.units
  );

const interpolate = (
  points: { x: number; y: number }[],
  x: number
): number | null => {
  if (
    points.length === 0 ||
    x < points[0].x ||
    x > points[points.length - 1].x
  ) {
    return null;
  }

  const exactPoint = points.find((point) => point.x === x);
  if (exactPoint) return exactPoint.y;

  const upperIndex = points.findIndex((point) => point.x > x);
  if (upperIndex <= 0) return null;
  const lower = points[upperIndex - 1];
  const upper = points[upperIndex];
  if (upper.x === lower.x) return lower.y;

  const progress = (x - lower.x) / (upper.x - lower.x);
  return lower.y + progress * (upper.y - lower.y);
};

export const calculateSeriesGraph = (
  series: Record<string, number>[],
  variables: GraphVariable[],
  legend: GraphLegend,
  controls: GraphControls
): GraphLegendResult => {
  const controlsError = validateGraphControls(controls);
  if (controlsError) {
    return { legendId: legend.id, points: [], error: controlsError };
  }

  const xVariable = variables.find(
    (variable) => variable.key === legend.xKey
  );
  const yVariable = variables.find(
    (variable) => variable.key === legend.yKey
  );
  if (!xVariable || !yVariable) {
    return {
      legendId: legend.id,
      points: [],
      error: "Select a valid X variable and Y variable.",
    };
  }
  if (legend.xKey === legend.yKey) {
    return {
      legendId: legend.id,
      points: [],
      error: "X and Y must be different variables.",
    };
  }

  const sourcePoints = series
    .map((row) => ({
      x: valueFromBaseUnit(xVariable, row[legend.xKey]),
      y: valueFromBaseUnit(yVariable, row[legend.yKey]),
    }))
    .filter(
      (point) => Number.isFinite(point.x) && Number.isFinite(point.y)
    )
    .sort((first, second) => first.x - second.x);
  const trackingValues = createTrackingValues(controls);
  if (!trackingValues) {
    return {
      legendId: legend.id,
      points: [],
      error:
        "Adjust the cursor increment to create between 2 and 1000 tracking points.",
    };
  }

  const points = trackingValues.map((x) => ({
    x,
    y: interpolate(sourcePoints, x),
  }));
  if (points.every((point) => point.y === null)) {
    return {
      legendId: legend.id,
      points,
      error: "The selected range produced no graph values.",
    };
  }

  return { legendId: legend.id, points };
};
